<script lang="ts">
import { templateRef, tryOnUnmounted, useElementSize } from '@vueuse/core';
import { store as scrollStore } from 'components/SmoothScroll/store';
import { store as loaderStore } from 'components/ui/Loader/store';
import settings from 'config';
import { store as themeStore } from 'pages/Thematique/store';
import { store as i18nStore } from 'plugins/i18n/store';
import emitter, { EVENTS } from 'plugins/store/events';
import { store as appStore } from 'store/app';
import { store as globalStore } from 'store/globals/index';
import useFlow from 'utils/flow';
import { computed, defineComponent, ref, toRefs, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import TheNav from "./Navigation.vue";
import { store as NavStore } from './store';

const TheHeader = defineComponent({
  name: "TheHeader",
  components: {
    TheNav
  },
  setup(props) {
    const { onInitUi } = useFlow();
    const el = templateRef<HTMLElement | null>('el', null);
    const { t } = useI18n();
    const { height } = useElementSize(el);
    const { done } = toRefs(loaderStore.state);
    const route = useRoute();
    const disabled = computed(() => appStore.disabled);
    const isThemeRoute = ref<boolean>(false);
    const textColor = ref<string>('#192096');
    const variant = ref<string>('default');
    const i18nLocale: Koddein.Locale = i18nStore.locale as Koddein.Locale;
    const { is404 } = toRefs(appStore.state);
    const scrollDirection = ref(0);
    const noGradient = ref<boolean>(false);

    let RAF: number;

    watch(height, (h) => NavStore.setHeight(h + 26), { deep: true, immediate: true });

    onInitUi(() => {
      watch(
        () => route,
        () => {
          NavStore.setHidden(false);
          if (route?.name) {
            noGradient.value = route.name === "home";

            switch (route.name as string) {
              case "collection":
                appStore.setAppBgColor("255, 234, 249");  // #FFEAF9
                textColor.value = '#192096';
                themeStore.setCurrent(undefined);
                break;

              case "bio":
                appStore.setAppBgColor("25, 32, 150"); // #192096;
                textColor.value = '#E5F0F8';
                themeStore.setCurrent(undefined);
                break;

              case "credit":
                appStore.setAppBgColor("229, 240, 248"); // #e5f0f8;

                NavStore.setHidden(true);
                break;

              case "thematique":
                NavStore.setHidden(true);
                break;
              case "home":
              case "object":
                appStore.setAppBgColor("255, 234, 249");  // #FFEAF9
                textColor.value = '#192096';
                NavStore.setHidden(true);
                break;
            }
          }
        },
        {
          deep: true,
          immediate: true
        }
      )

      watch(
        () => themeStore.current,
        (current) => {
          if (!current) return;
          variant.value = current?.id || 'default';
          textColor.value = '#192096';
        },
        { deep: true, immediate: true }
      )

      if (!is404.value) checkScroll();
    });

    const checkScroll = () => {
      RAF = requestAnimationFrame(checkScroll);

      // force to hide the nav on theme route
      if (isThemeRoute.value) {
        scrollDirection.value = -1 as number;
      }
      else {
        scrollDirection.value = (scrollStore.getScroll() < 100) ? 0 : scrollStore.getDirection() as number;
      }
    }

    tryOnUnmounted(() => {
      cancelAnimationFrame(RAF);
    })

    // Toggle Nav
    const toggleNav = () => {
      NavStore.openState(!NavStore.isOpen);
    }

    // Foce Close Nav
    const closeNav = (e : PointerEvent, url:string) => {
      e?.preventDefault();

      if(url === route.path) return;

      emitter.emit(EVENTS.PAGE_TRANSITION, {url, bgColor: themeStore.getBgColorFromURL(url)});
      NavStore.openState(false);
    }

    return {
      ...toRefs(props),
      t,
      loaderDone: done,
      variant,
      disabled,
      noGradient,
      routeName: computed( () => route.path),
      localeUri: i18nLocale?.uri || '/',
      height: computed(() => NavStore.height),
      settings,
      isOpen: computed(() => NavStore.isOpen),
      navigations: computed(() => globalStore.navigations),
      scrollDirection,
      isThemeRoute,
      gradientColor1: computed(() => `rgba(${appStore.bgColor}, 0)`),
      gradientColor2: computed(() => `rgba(${appStore.bgColor}, .79)`),
      gradientColor3: computed(() => `rgba(${appStore.bgColor}, 1)`),
      textColor,
      isHidden: computed(() => NavStore.hidden),
      toggleNav,
      closeNav,
    }
  }
});

export default TheHeader
</script>

<template>
  <header ref="el"
          class="TheHeader"
          :data-disabled="disabled"
          :data-isopen="isOpen"
          :data-variant="variant"
          :class="['TheHeader', { 'hidden': !loaderDone }]"
          :data-dark="false"
          :data-thematique="isHidden"
          :data-direction="scrollDirection"
  >
    <div class="row _container">
      <nav class="main-nav">
        <ul class="links">
          <li v-for="(item, index) in navigations.main"
              :key="`link-${index}`"
              class="link-wrapper"
          >
            <ui-button theme="nav"
                       class="link"
                       :label="item.label"
                       :href="item.url"
                       :data-selected="item.url === routeName"
                       :variant="variant"
                       tag="a"
                       @click="closeNav($event, item.url)"
            />
          </li>
          <li class="link-wrapper">
            <ui-button theme="nav"
                       class="link"
                       :label="t('LABELS.themes')"
                       :variant="variant"
                       :expand="true"
                       :selected="isOpen"
                       @click="toggleNav"
            />
          </li>
        </ul>
      </nav>
    </div>
    <div v-show="!noGradient || isOpen" class="gradient" />
  </header>
  <TheNav />

  <component :is="'style'">
    :root {
    --header-height: {{ height }}px;
    }
  </component>
</template>


<style lang="stylus" scoped>
  .TheHeader
    position fixed
    top 9px
    left $gutter
    color v-bind(textColor)
    width calc('100vw - 20px')
    padding 10px 0 11px 0
    margin 0 auto
    z-index $z-header + 1
    trans(color, $duration:1s)
    trans(transform, $duration:1s)
    &[data-disabled="true"]
      pointer-events none
    &.hidden
      visibility hidden
      pointer-events none
    &.-blurry
      trans(opacity, $duration:1s)
      transition-delay 1s
      opacity 0

    &:before, &:after
      width 100%
      height 1px
      background-color currentColor
      display block
      position absolute
      content ''
      trans(background-color, $duration:0.5s)
      pointer-events none
      opacity .6
    &:before
      top 0px
    &:after
      bottom 0px

    &[data-direction="1"]
      transform translateY(-120%)
      transition-duration 0.75s
      
    &[data-direction="1"][data-thematique="true"], &[data-direction="0"][data-thematique="true"]
      .gradient
        opacity 0


    &[data-direction="-1"]
      .gradient
        opacity 1
    
    &[data-thematique="true"]
      transform translateY(-130%)
      addVariantProps(color, false)     
      .gradient
        opacity 0   

    .gradient
      position absolute
      top 'calc(%s * -1)' % ($gutter)
      left 'calc(%s * -1)' % ($gutter)
      width 100vw
      z-index -1
      rp(height, 90px)
      trans(opacity)
      trans(height)
      trans(background, $duration:1.5s)
      pointer-events none
      background 'linear-gradient(0deg, %s 0%, %s 49%, %s 100%)' % (v-bind(gradientColor1) v-bind(gradientColor2) v-bind(gradientColor3))
      [data-isopen="true"]&
        rp(height, 90px)

    .main-nav
      position relative
      width 100%

    .main-nav
      position relative
      width 100%

      .links
        f(row)
        line-height 0 
      
      .link
        f-style(text16)
        [data-thematique="false"]&
          color v-bind(textColor)
</style>
